import React from 'react';
import { getIn, useFormikContext } from 'formik';

import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CheckboxWithButtonWrapperFormik from '~/components/core/Formik/CheckboxWithButtonWrapperFormik';
import { useFormikFieldError } from '~/components/core/Formik/formikErrorUtils';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextFieldFormik from '~/components/TextFieldFormik';
import Collapse from '~/components/ui/atoms/Collapse';
import VITESSE_APIS from '~/server_shared/generated-types/VITESSE_APIS';
import VITESSE_VERIFICATION_METHOD_SOURCES from '~/server_shared/generated-types/VITESSE_VERIFICATION_METHOD_SOURCES';
import type {
  VitesseProgrammeCheckArguments,
  VitesseProgrammeCheckResultVitessePassphraseMethodOption,
} from '~/server_shared/openapi/data-contracts';

import { useSysconfig } from '../../../SystemConfigurationScreen';
import type { FundAccountSourceOrganization } from '../FundAccountSourceField';
import { isFundAccountResolvedByRules } from '../FundAccountSourceField';
import SectionHeading from '../SectionHeading';
import VitesseProgrammeVerification from '../VitesseProgrammeVerification';

import type { VitesseConfigurationValues, VitesseProviderSpecificConfig } from './verificationConfig';

import { useStyles } from '../../../../../assets/styles';
import styles from '../PaymentsConfigurationForm.module.scss';

/**
 * The Vitesse fields, rendered by `PaymentProviderSelect` as the grid items of the section it owns while
 * Vitesse is the selected provider. Whether Vitesse is offered at all, and whether it is selected, is the
 * select's decision — this component is only mounted once it is.
 */
interface VitesseProviderFieldsProps {
  currentPaymentMethod: string;
  isCustomMethod: boolean;
  readOnly?: boolean;
}

const PASSPHRASE_SOURCE_HELPER_TEXT =
  'The payee must already know this value — it has to be shared with them outside the payment link, and the payment cannot be issued while it is empty on the claim.';

/**
 * The Formik wiring `TextFieldFormik` did for a select, for the `SingleSelectField` that replaces it:
 * the current value, committing a choice, marking the field touched on blur (a MUI select's `onBlur`
 * carries neither an id nor a name, so `handleBlur` cannot resolve which field to touch), and showing
 * the validation message in place of the helper text.
 *
 * The value is handed over exactly as `TextFieldFormik` handed it over — not defaulted to `''` —
 * because MUI freezes whether an input is controlled at mount, so which of the two an empty select
 * mounts as has to stay as it was.
 */
const useFormikSelectField = (id: string, helperText?: string) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext<VitesseConfigurationValues>();
  const { error, hasError } = useFormikFieldError(id);
  const value: string | undefined = getIn(values, id);

  return {
    id,
    value,
    onChange: (newValue: string) => setFieldValue(id, newValue),
    onBlur: () => setFieldTouched(id, true),
    error: hasError,
    helperText: error || helperText,
  };
};

function VitesseProviderFields({ currentPaymentMethod, isCustomMethod, readOnly }: VitesseProviderFieldsProps) {
  const { isSubmitting, values, initialValues, setFieldTouched } = useFormikContext<VitesseConfigurationValues>();
  const { classes } = useStyles();
  // `useSysconfig` comes from an untyped JS context (`createContext({})`), so TypeScript sees `{}`.
  // Naming the one field this screen reads keeps the read typed without suppressing the error.
  const { organization } = useSysconfig() as { organization: FundAccountSourceOrganization };
  const isUkBankTransfer = currentPaymentMethod === 'uk_bank_transfer';
  // With the fund-account engine resolving the send account per payment, Vitesse's own configured
  // account is not the one being used, so there is nothing to ask for. Hidden rather than cleared: the
  // server relaxes the requirement without forbidding the key, and a stored id has to survive a
  // provider-account -> rules -> provider-account round trip within one unsaved dialog session.
  //
  // The flag half of the predicate is what keeps a block that outlived the engine from hiding a field
  // the server still requires.
  const isFundAccountSourceRules = isFundAccountResolvedByRules({
    organization,
    fundAccount: values.method_configuration?.fund_account,
  });
  // Grandfathering reads the *persisted* value, not the live one: the legacy Transactions option must
  // stay available to an organization already saved on it, but must not be selectable again once the
  // organization has been moved to Payment Requests.
  const persistedVitesseApi = initialValues?.method_configuration?.payment_provider_specific?.vitesse_api;
  const isLegacyTransactionsAvailable = persistedVitesseApi === 'transactions';
  const paymentProviderSpecific: VitesseProviderSpecificConfig =
    values.method_configuration?.payment_provider_specific || {};
  const isPassphraseEnabled = !!paymentProviderSpecific.vitesse_verification_passphrase_enabled;
  const passphraseSource = paymentProviderSpecific.vitesse_verification_method_source;
  // The programme's own passphrase definitions, filled in by the configuration check. Until it has run
  // the method id stays a text field, so a stored id is never silently dropped for not being in a list
  // we have not fetched yet.
  const [passphraseMethodOptions, setPassphraseMethodOptions] = React.useState<
    VitesseProgrammeCheckResultVitessePassphraseMethodOption[]
  >([]);
  // Exactly the keys the verify endpoint accepts (it forbids extras), carrying the values on screen
  // rather than the saved row so a config can be checked before it is committed.
  const vitesseProgrammeCheckArguments: VitesseProgrammeCheckArguments = {
    vitesse_programme_id: paymentProviderSpecific.vitesse_programme_id || '',
    vitesse_account_id: paymentProviderSpecific.vitesse_account_id || null,
    vitesse_verification_sms_enabled: !!paymentProviderSpecific.vitesse_verification_sms_enabled,
    vitesse_verification_passphrase_enabled: isPassphraseEnabled,
    vitesse_verification_method_id: paymentProviderSpecific.vitesse_verification_method_id || null,
    vitesse_verification_method_source: passphraseSource || null,
    vitesse_verification_method_field_key: paymentProviderSpecific.vitesse_verification_method_field_key || null,
  };

  const vitesseApiField = useFormikSelectField('method_configuration.payment_provider_specific.vitesse_api');
  const passphraseMethodField = useFormikSelectField(
    'method_configuration.payment_provider_specific.vitesse_verification_method_id'
  );
  const passphraseSourceField = useFormikSelectField(
    'method_configuration.payment_provider_specific.vitesse_verification_method_source',
    PASSPHRASE_SOURCE_HELPER_TEXT
  );

  React.useEffect(() => {
    // Touched up front so a rule that is already broken is shown while the operator is still in the
    // section, rather than only after the field has been visited.
    [
      'vitesse_account_id',
      'vitesse_programme_id',
      'vitesse_verification_sms_enabled',
      'vitesse_verification_passphrase_enabled',
      'vitesse_verification_method_id',
      'vitesse_verification_method_source',
      'vitesse_verification_method_field_key',
      'vitesse_api',
    ].forEach((vitesseField) => {
      setFieldTouched(`method_configuration.payment_provider_specific.${vitesseField}`, true);
    });
  }, [currentPaymentMethod, setFieldTouched]);

  return (
    <>
      {!isFundAccountSourceRules && (
        <Grid item xs={6}>
          <TextFieldFormik
            className={classes.textField}
            id="method_configuration.payment_provider_specific.vitesse_account_id"
            label="Account ID"
            disabled={readOnly || isSubmitting}
            fullWidth
          />
        </Grid>
      )}
      {isUkBankTransfer && (
        <Grid item xs={6}>
          <SingleSelectField<string>
            {...vitesseApiField}
            className={classes.textField}
            label="Vitesse API"
            disabled={readOnly || isSubmitting}
            fullWidth
          >
            <MenuItem value="payment_requests">{VITESSE_APIS.payment_requests.desc}</MenuItem>
            {isLegacyTransactionsAvailable && (
              <MenuItem value="transactions">{VITESSE_APIS.transactions.desc}</MenuItem>
            )}
          </SingleSelectField>
        </Grid>
      )}
      {isCustomMethod && (
        <>
          <Grid item xs={6}>
            <TextFieldFormik
              className={classes.textField}
              id="method_configuration.payment_provider_specific.vitesse_programme_id"
              label="Programme ID"
              disabled={readOnly || isSubmitting}
              fullWidth
            />
          </Grid>
          <SectionHeading
            title="Payee Verification"
            subtitle="How the payee proves who they are before choosing how to be paid. With neither option selected, Vitesse applies the programme's default method."
            subtitleClassName="text-slate-900"
          />
          {/* One option per row rather than side by side: the passphrase side grows by three fields
              when ticked, and as a half-width pair that left the SMS column as a tall empty gap. */}
          <Grid item xs={12}>
            <CheckboxWithButtonWrapperFormik
              id="method_configuration.payment_provider_specific.vitesse_verification_sms_enabled"
              text="SMS code"
              disabled={readOnly || isSubmitting}
              className={styles.checkbox}
            />
          </Grid>
          {/* Checkbox-reveals-its-settings, mirroring the ClaimSearch configuration steps
              (`Integrations/ClaimSearch/.../InitialNotification`): the checkbox stays a plain sibling
              so it aligns with the SMS one above, and `Collapse` animates the panel it owns. The
              panel is boxed rather than marked with a left rule so the fields read as belonging to
              this option and not to the SMS checkbox above it. */}
          <Grid item xs={12}>
            <CheckboxWithButtonWrapperFormik
              id="method_configuration.payment_provider_specific.vitesse_verification_passphrase_enabled"
              text="Passphrase"
              disabled={readOnly || isSubmitting}
              className={styles.checkbox}
            />
            <Collapse in={isPassphraseEnabled} timeout="auto" unmountOnExit>
              <div className="mt-8 rounded border border-solid border-slate-200 p-16">
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    {passphraseMethodOptions.length ? (
                      <SingleSelectField<string>
                        {...passphraseMethodField}
                        className={classes.textField}
                        label="Passphrase Method"
                        disabled={readOnly || isSubmitting}
                        fullWidth
                      >
                        {passphraseMethodOptions.map(({ method_id, name, is_default }) => (
                          <MenuItem key={method_id} value={method_id}>
                            {[name || method_id, is_default ? '(programme default)' : null].filter(Boolean).join(' ')}
                          </MenuItem>
                        ))}
                      </SingleSelectField>
                    ) : (
                      <TextFieldFormik
                        className={classes.textField}
                        id="method_configuration.payment_provider_specific.vitesse_verification_method_id"
                        label="Verification Method ID"
                        helperText="Check against Vitesse to pick from the programme's own methods instead of pasting an ID."
                        disabled={readOnly || isSubmitting}
                        fullWidth
                      />
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <SingleSelectField<string>
                      {...passphraseSourceField}
                      className={classes.textField}
                      label="Passphrase Source"
                      disabled={readOnly || isSubmitting}
                      fullWidth
                    >
                      {Object.entries(VITESSE_VERIFICATION_METHOD_SOURCES).map(([sourceKey, { desc }]) => (
                        <MenuItem key={sourceKey} value={sourceKey}>
                          {desc}
                        </MenuItem>
                      ))}
                    </SingleSelectField>
                  </Grid>
                  {passphraseSource === 'incident_configured_field' && (
                    <Grid item xs={6}>
                      <TextFieldFormik
                        className={classes.textField}
                        id="method_configuration.payment_provider_specific.vitesse_verification_method_field_key"
                        label="Incident Configured Field Key"
                        disabled={readOnly || isSubmitting}
                        fullWidth
                      />
                    </Grid>
                  )}
                </Grid>
              </div>
            </Collapse>
          </Grid>
          <SectionHeading
            title="Vitesse configuration check"
            subtitle="Checks this configuration against the Vitesse programme — the send account, payout methods, trust methods and payee verification — so a mismatch surfaces here instead of on the first payment."
            subtitleClassName="text-slate-900"
          />
          <Grid item xs={12}>
            <VitesseProgrammeVerification
              organizationId={organization.id}
              config={vitesseProgrammeCheckArguments}
              disabled={readOnly || isSubmitting || !vitesseProgrammeCheckArguments.vitesse_programme_id}
              onPassphraseMethodsLoaded={setPassphraseMethodOptions}
            />
          </Grid>
        </>
      )}
    </>
  );
}

export default VitesseProviderFields;
